import { Icon } from '@/components/Icon'
import { RefreshCw, X } from 'lucide-react'
import React from 'react'
import { formatFileSize } from '@/lib/utils'

// 文件状态类型
export type FileStatus =
  | 'WAITING' // 等待上传
  | 'UPLOADING' // 上传中
  | 'SUCCESS' // 上传成功
  | 'ERROR' // 上传失败
  | 'processing' // 处理中
  | 'completed' // 处理完成

// 基础文件信息
export interface BaseFile {
  id: string
  name: string
  size: number
  file?: File
}

// 上传文件信息
export interface UploadFile extends BaseFile {
  progress: number
  status: FileStatus
  error?: string
}

// 处理文件信息
export interface ProcessFile extends BaseFile {
  progress: number
  status: FileStatus
  error?: string
}

// 文件卡片类型
export type FileCardType = 'upload' | 'process'

// 文件卡片属性
export interface FileCardProps {
  /** 文件数据 */
  file: UploadFile | ProcessFile
  /** 卡片类型 */
  type: FileCardType
  /** 是否显示删除按钮 */
  showDelete?: boolean
  /** 删除回调 */
  onDelete?: (id: string) => void
  /** 重试回调 */
  onRetry?: (id: string) => void
  /** 自定义类名 */
  className?: string
}

const FileCard: React.FC<FileCardProps> = ({
  file,
  type,
  showDelete = true,
  onDelete,
  onRetry,
  className,
}) => {
  // 获取文件类型图标
  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    switch (ext) {
      case 'pdf':
        return 'pdf'
      case 'doc':
      case 'docx':
        return 'doc'
      case 'xls':
      case 'xlsx':
        return 'excel'
      default:
        return 'text'
    }
  }

  // 获取背景样式
  const getBackgroundStyle = () => {
    if (type === 'upload') {
      return 'bg-[rgba(78,87,254,0.02)]'
    }
    return 'bg-[rgba(78,87,254,0.02)]'
  }

  // 添加动画样式
  React.useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      @keyframes shine {
        to {
          background-position: 200% center;
        }
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // 渲染上传状态
  const renderUploadStatus = (file: UploadFile) => {
    const isUploading = file.status === 'UPLOADING'

    return (
      <div className="flex items-center">
        {/* 上传进度背景 */}
        {isUploading && (
          <div className="flex items-center">
            <div className="w-24 bg-[#4E57FE1A] h-1 rounded-full overflow-hidden mr-2">
              <div
                className="h-full bg-[#4E57FE] transition-all duration-300"
                style={{ width: `${file.progress}%` }}
              />
            </div>
            <span className="text-sm text-[#666666]">{file.progress}%</span>
          </div>
        )}
      </div>
    )
  }

  // 渲染处理状态
  const renderProcessStatus = (file: ProcessFile) => {
    return (
      <div className="flex items-center">
        {file.status === 'processing' ? (
          <div className="flex items-center">
            <div
              className="flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-600 relative overflow-hidden"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                backgroundSize: '200% 100%',
                animation: 'shine 2s linear infinite',
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              <span>处理中...</span>
            </div>
          </div>
        ) : file.status === 'completed' ? (
          <div className="px-3 py-1 rounded-full bg-green-100 text-green-600">
            处理完成
          </div>
        ) : (
          <div className="px-3 py-1 rounded-full bg-gray-100 text-gray-600">
            等待处理
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`w-full h-[58px] border border-[rgba(78,87,254,0.11)] rounded-[8px] ${getBackgroundStyle()} ${
        className || ''
      }`}
    >
      <div className="h-full px-4 flex items-center">
        {/* 文件图标 */}
        <div className="flex-shrink-0 mr-3">
          <Icon
            name={getFileIcon(file.name)}
            size={32}
            className="text-[#4E57FE]"
          />
        </div>

        {/* 文件信息 */}
        <div className="flex-1 min-w-0">
          <div className="text-base text-[#333333] font-medium truncate">
            {file.name}
          </div>
          <div className="text-xs text-[#999999] mt-1">
            {formatFileSize(file.size)}
          </div>
        </div>

        {/* 状态显示 */}
        <div className="flex-shrink-0 flex items-center">
          <div className="flex items-center">
            {type === 'upload' && renderUploadStatus(file as UploadFile)}
            {type === 'process' && renderProcessStatus(file as ProcessFile)}
          </div>
          {/* 操作按钮 */}
          <div className="flex items-center">
            {/* 重试按钮 */}
            {file.status === 'ERROR' && onRetry && (
              <button
                onClick={() => onRetry(file.id)}
                className="ml-3 w-6 h-6 flex items-center justify-center text-[#4E57FE] hover:text-[#4E57FE] transition-colors"
                title="重试上传"
                aria-label="重试上传"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            {/* 删除按钮 */}
            {showDelete && (
              <button
                onClick={() => onDelete?.(file.id)}
                className="ml-3 w-6 h-6 flex items-center justify-center text-[#999999] hover:text-[#FF4D4F] transition-colors"
                title="删除文件"
                aria-label="删除文件"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FileCard
